import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://compliance-backend.cloudsanalytics.ai';

class ComplianceApiService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor for Cognito token authentication
    this.api.interceptors.request.use(
      (config) => {
        const accessToken = localStorage.getItem('cognito_token');
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        } else {
          // If no token, reject the request
          return Promise.reject(new Error('No authentication token available'));
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Fetch compliance alerts for a specific agent
   * @param {string} agentId - The agent ID to fetch compliance alerts for (defaults to '001')
   * @param {Object} params - Query parameters (page, size, etc.)
   * @returns {Promise<Object>} Compliance alerts response with pagination
   */
  async getComplianceAlerts(agentId = '001', params = {}) {
    try {
      const response = await this.api.get(`/alerts/${agentId}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching compliance alerts for agent ${agentId}:`, error);
      throw error;
    }
  }

  /**
   * Process compliance alerts to extract compliance standards statistics
   * @param {Array} alerts - Array of compliance alerts
   * @returns {Object} Processed compliance statistics
   */
  processComplianceData(alerts) {
    if (!alerts || !Array.isArray(alerts)) {
      return {
        totalAlerts: 0,
        complianceStandards: {},
        severityDistribution: {},
        recentAlerts: []
      };
    }

    const complianceStandards = {};
    const severityDistribution = {};
    let totalAlerts = alerts.length;

    alerts.forEach(alert => {
      const rule = alert.rule || {};
      
      // Count severity levels
      const level = rule.level || 0;
      const severity = this.getSeverityLabel(level);
      severityDistribution[severity] = (severityDistribution[severity] || 0) + 1;

      // Process compliance standards
      const standards = ['pci_dss', 'hipaa', 'gdpr', 'nist_800_53', 'tsc', 'gpg13'];
      standards.forEach(standard => {
        if (rule[standard] && Array.isArray(rule[standard])) {
          if (!complianceStandards[standard]) {
            complianceStandards[standard] = {
              name: this.getStandardName(standard),
              count: 0,
              violations: new Set()
            };
          }
          complianceStandards[standard].count += 1;
          rule[standard].forEach(violation => {
            complianceStandards[standard].violations.add(violation);
          });
        }
      });
    });

    // Convert violations sets to arrays and get counts
    Object.keys(complianceStandards).forEach(key => {
      complianceStandards[key].violations = Array.from(complianceStandards[key].violations);
      complianceStandards[key].violationCount = complianceStandards[key].violations.length;
    });

    return {
      totalAlerts,
      complianceStandards,
      severityDistribution,
      recentAlerts: alerts.slice(0, 10).map(alert => ({
        id: alert.id,
        timestamp: alert.timestamp,
        description: alert.rule?.description || 'No description',
        level: alert.rule?.level || 0,
        agent: alert.agent?.name || 'Unknown',
        standards: this.extractAlertStandards(alert.rule)
      }))
    };
  }

  /**
   * Get severity label from numeric level
   * @param {number} level - Numeric severity level
   * @returns {string} Severity label
   */
  getSeverityLabel(level) {
    if (level >= 10) return 'Critical';
    if (level >= 7) return 'High';
    if (level >= 4) return 'Medium';
    if (level >= 1) return 'Low';
    return 'Info';
  }

  /**
   * Get color for compliance standards (for donut charts)
   * @param {string} standard - Standard name
   * @returns {string} Color hex code
   */
  getStandardColor(standard) {
    const colors = {
      'PCI DSS': '#FF6B6B',
      'HIPAA': '#4ECDC4', 
      'GDPR': '#45B7D1',
      'NIST 800-53': '#96CEB4',
      'TSC': '#FFEAA7',
      'GPG13': '#DDA0DD'
    };
    return colors[standard] || '#95A5A6';
  }

  /**
   * Get color for severity levels (for donut charts)
   * @param {string} severity - Severity level
   * @returns {string} Color hex code
   */
  getSeverityColor(severity) {
    const colors = {
      'Critical': '#E74C3C',
      'High': '#E67E22', 
      'Medium': '#F39C12',
      'Low': '#27AE60',
      'Info': '#3498DB'
    };
    return colors[severity] || '#95A5A6';
  }

  /**
   * Get compliance standards data formatted for donut charts
   * @param {Array} alerts - Array of compliance alerts
   * @returns {Array} Formatted data for donut chart
   */
  getComplianceStandardsChart(alerts) {
    const processed = this.processComplianceData(alerts);
    return Object.entries(processed.complianceStandards).map(([key, data]) => ({
      name: data.name,
      value: data.count,
      count: data.count,
      percentage: processed.totalAlerts > 0 ? 
        ((data.count / processed.totalAlerts) * 100).toFixed(1) : 0,
      fill: this.getStandardColor(data.name)
    }));
  }

  /**
   * Get severity distribution data formatted for donut charts
   * @param {Array} alerts - Array of compliance alerts
   * @returns {Array} Formatted data for donut chart
   */
  getSeverityDistributionChart(alerts) {
    const processed = this.processComplianceData(alerts);
    return Object.entries(processed.severityDistribution).map(([severity, count]) => ({
      name: severity,
      value: count,
      count: count,
      percentage: processed.totalAlerts > 0 ? 
        ((count / processed.totalAlerts) * 100).toFixed(1) : 0,
      fill: this.getSeverityColor(severity)
    }));
  }

  /**
   * Get enhanced recent alerts with geo location and additional data
   * @param {Array} alerts - Array of compliance alerts
   * @returns {Array} Enhanced alerts data
   */
  getEnhancedRecentAlerts(alerts) {
    if (!alerts || !Array.isArray(alerts)) return [];
    
    return alerts.slice(0, 10).map(alert => ({
      id: alert.id,
      timestamp: alert.timestamp || alert['@timestamp'],
      description: alert.rule?.description || 'No description',
      level: alert.rule?.level || 0,
      severity: this.getSeverityLabel(alert.rule?.level || 0),
      agent: {
        name: alert.agent?.name || 'Unknown',
        ip: alert.agent?.ip || 'Unknown',
        id: alert.agent?.id || 'Unknown'
      },
      location: alert.location,
      geoLocation: alert.GeoLocation,
      ruleId: alert.rule?.id,
      groups: alert.rule?.groups || [],
      standards: this.extractAlertStandards(alert.rule),
      data: alert.data,
      fullLog: alert.full_log
    }));
  }

  /**
   * Get human-readable standard name
   * @param {string} standard - Standard key
   * @returns {string} Human-readable name
   */
  getStandardName(standard) {
    const names = {
      'pci_dss': 'PCI DSS',
      'hipaa': 'HIPAA',
      'gdpr': 'GDPR',
      'nist_800_53': 'NIST 800-53',
      'tsc': 'TSC',
      'gpg13': 'GPG13'
    };
    return names[standard] || standard.toUpperCase();
  }

  /**
   * Extract compliance standards from alert rule
   * @param {Object} rule - Alert rule object
   * @returns {Array} Array of compliance standards
   */
  extractAlertStandards(rule) {
    if (!rule) return [];
    
    const standards = [];
    const standardKeys = ['pci_dss', 'hipaa', 'gdpr', 'nist_800_53', 'tsc', 'gpg13'];
    
    standardKeys.forEach(key => {
      if (rule[key] && Array.isArray(rule[key]) && rule[key].length > 0) {
        standards.push({
          name: this.getStandardName(key),
          violations: rule[key]
        });
      }
    });
    
    return standards;
  }
}

// Create and export a singleton instance
const complianceApiService = new ComplianceApiService();
export default complianceApiService;
